import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { EditorLeaveGuard } from '../hooks/use-editor-leave-guard';
import { useWebsiteT } from '../hooks/use-website-t';

export interface EditorLeaveDialogProps {
  readonly guard: EditorLeaveGuard;
}

const CAUSE_KEYS: Readonly<Record<'failed' | 'conflict' | 'rejected', string>> = {
  failed: 'admin.websiteTemplates.editor.save.failed',
  conflict: 'admin.websiteTemplates.editor.save.conflict',
  rejected: 'admin.websiteTemplates.editor.save.rejected',
};

/**
 * Epic #228 P4 (PD11): the one question asked when leaving the editor would
 * lose work. Stay, Escape and a click outside keep the merchant here; only
 * "Leave without saving" drops what is unsaved.
 */
export function EditorLeaveDialog({ guard }: EditorLeaveDialogProps): React.JSX.Element {
  const { t, dir } = useWebsiteT();
  const { state } = guard;
  const saving = state === 'saving';
  const cause = state === 'failed' || state === 'conflict' || state === 'rejected' ? state : null;

  return (
    <AlertDialog
      open={state !== 'closed'}
      onOpenChange={(next) => {
        if (!next) guard.stay();
      }}
    >
      <AlertDialogContent dir={dir} data-testid="editor-leave-dialog" data-stage={state}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {saving
              ? t('admin.websiteTemplates.leave.saving')
              : t('admin.websiteTemplates.leave.title')}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-1">
            {saving ? (
              <span role="status" className="flex items-center gap-2">
                <Loader2 aria-hidden="true" className="size-4 shrink-0 motion-safe:animate-spin" />
                {t('admin.websiteTemplates.editor.save.saving')}
              </span>
            ) : (
              <>
                {cause === null ? null : (
                  <span className="block" data-testid="editor-leave-cause">
                    {t(CAUSE_KEYS[cause])}
                    {/* F2: say what keeping this window's version does. */}
                    {cause === 'conflict'
                      ? ` ${t('admin.websiteTemplates.editor.save.conflictHelp')}`
                      : null}
                  </span>
                )}
                <span className="block">{t('admin.websiteTemplates.leave.body')}</span>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel data-testid="editor-leave-stay">
            {t('admin.websiteTemplates.leave.stay')}
          </AlertDialogCancel>
          <Button
            type="button"
            // Waiting (and a recoverable failure) is the default; leaving is not the loudest action.
            variant={
              saving || cause === 'failed' || cause === 'conflict' ? 'outline' : 'destructive'
            }
            data-testid="editor-leave-leave"
            onClick={guard.leave}
          >
            {t('admin.websiteTemplates.leave.leave')}
          </Button>
          {cause === 'failed' ? (
            <Button type="button" data-testid="editor-leave-retry" onClick={guard.retry}>
              {t('admin.websiteTemplates.leave.retry')}
            </Button>
          ) : cause === 'conflict' ? (
            <Button type="button" data-testid="editor-leave-reload" onClick={guard.retry}>
              {t('admin.websiteTemplates.leave.keepMine')}
            </Button>
          ) : null}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
